/**
 * Recipient Reviews — cross-article trust ledger management.
 *
 * The recipient_review table (assayer_notification_recipient_review) is the
 * source of truth for "is it OK to email <recipient> in connection with
 * <entity>?". Two write paths feed it:
 *
 *   1. The draft drawer's Approve recipients / Approve & send buttons —
 *      in-context decision-making while reviewing a specific draft.
 *   2. This page — the cross-article view, used to pre-approve trusted
 *      journalists in advance OR audit / reject decisions taken in haste.
 *
 * The page is intentionally lean: list rows with status badges, per-row
 * Approve/Reject buttons for pending, bulk-action toolbar when multiple
 * rows selected. No editing of the (email, entity) pair itself — that's
 * derived from the announcement extractor.
 */

import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TableScroll } from '../components/ui/table-scroll.js';
import {
  ArrowLeft, CheckCircle2, XCircle, Search, X, RefreshCw, AlertCircle,
} from 'lucide-react';
import {
  listRecipientReviews,
  approveRecipientReview,
  rejectRecipientReview,
  bulkApproveRecipientReviews,
  bulkRejectRecipientReviews,
  type RecipientReviewRow,
  type RecipientReviewStatus,
} from '../lib/api/notifications.js';

type StatusFilter = RecipientReviewStatus | 'all';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'pending',  label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all',      label: 'All' },
];

const STATUS_BADGE: Record<RecipientReviewStatus, { label: string; cls: string }> = {
  pending:  { label: 'Pending',  cls: 'bg-grey-100 text-grey-700 border border-grey-200' },
  approved: { label: 'Approved', cls: 'bg-success-50 text-success-700 border border-success-200' },
  rejected: { label: 'Rejected', cls: 'bg-danger-50 text-danger-700 border border-danger-200' },
};

export default function RecipientReviewsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  const debouncedSearch = useDebounced(searchInput.trim(), 300);
  const searchActive = debouncedSearch.length > 0;

  // Searching hides the status tabs and says "across all statuses" — so it
  // must actually send 'all'. It sent the (invisible) status filter until
  // 2026-08-12, which meant searching for anyone already approved or
  // rejected returned "no pairs match" — the ledger looked empty for exactly
  // the rows a search is usually trying to find.
  const effectiveStatus = searchActive ? 'all' : statusFilter;
  const queryKey = ['notifications', 'recipient-reviews', effectiveStatus, debouncedSearch];

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey,
    queryFn: () => listRecipientReviews({
      status: effectiveStatus,
      q: searchActive ? debouncedSearch : undefined,
      limit: 200,
    }),
    refetchInterval: 60_000,
  });

  const rows = data?.items ?? [];

  // Selection only applies to pending rows — approving an already-approved
  // pair is a no-op, but the UI shouldn't pretend it's an actionable state.
  const selectablePending = rows.filter(r => r.status === 'pending').map(r => r.review_id);
  const allPendingSelected = selectablePending.length > 0
    && selectablePending.every(id => selected.has(id));
  const someSelected = selected.size > 0;

  // Clear selection when filter changes (selected IDs would no longer match).
  useEffect(() => { setSelected(new Set()); }, [statusFilter, debouncedSearch]);

  const approveMut = useMutation({
    mutationFn: async ({ reviewId }: { reviewId: string }) => {
      return approveRecipientReview(reviewId);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'recipient-reviews'] }),
  });

  const rejectMut = useMutation({
    mutationFn: async ({ reviewId, notes }: { reviewId: string; notes?: string }) => {
      return rejectRecipientReview(reviewId, notes);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications', 'recipient-reviews'] }),
  });

  // Single bulk-endpoint call rather than a per-row loop — atomic on the
  // server, so navigate-away mid-call cannot leave the ledger half-written.
  // Response carries counts for a clean operator summary.
  const bulkApproveMut = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected);
      return await bulkApproveRecipientReviews(ids);
    },
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['notifications', 'recipient-reviews'] });
    },
  });

  const bulkRejectMut = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected);
      return await bulkRejectRecipientReviews(ids);
    },
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['notifications', 'recipient-reviews'] });
    },
  });

  const toggleRow = (reviewId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(reviewId)) next.delete(reviewId);
      else next.add(reviewId);
      return next;
    });
  };

  const toggleAllPending = () => {
    setSelected(prev => {
      if (allPendingSelected) return new Set();
      const next = new Set(prev);
      for (const id of selectablePending) next.add(id);
      return next;
    });
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-grey-500 mb-1">
            <Link to="/notifications/queue" className="inline-flex items-center gap-1 hover:text-grey-700">
              <ArrowLeft className="h-3.5 w-3.5" />
              Notifications queue
            </Link>
          </div>
          <h1 className="text-2xl font-semibold text-grey-900">Recipient Reviews</h1>
          <p className="text-sm text-grey-600 mt-1 max-w-3xl">
            Cross-article trust ledger. Each row is a <code className="px-1 py-0.5 bg-grey-100 rounded font-mono text-xs">(recipient, entity)</code> pair —
            approving here marks the pair as trusted across <strong>all future drafts</strong> involving that entity, so the
            recipient gets auto-included in future media-contact notifications without per-draft re-approval. Rejecting
            soft-locks the pair from future drafts (operator can override per-draft in the queue drawer).
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-grey-300 rounded hover:bg-grey-50"
        >
          <RefreshCw className={`h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      {/* Search */}
      <div className="mb-4 flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
          <input
            type="search"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search by email, name, organisation, entity, or ticker…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-700"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        {searchActive && (
          <span className="text-xs text-grey-500">Searching across all statuses</span>
        )}
      </div>

      {/* Status tabs — hidden while searching */}
      {!searchActive && (
        <div className="flex gap-2 mb-4 border-b border-grey-200">
          {STATUS_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setStatusFilter(opt.value)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                statusFilter === opt.value
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-grey-600 hover:text-grey-900'
              }`}
            >
              {opt.label}
              {statusFilter === opt.value && data?.count != null && (
                <span className="ml-2 text-xs text-grey-500">({data.count})</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Bulk action bar — only visible when at least one pending row is selected */}
      {someSelected && (
        <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50 p-3 flex items-center gap-3">
          <span className="text-sm text-primary-800 font-medium">
            {selected.size} pair{selected.size === 1 ? '' : 's'} selected
          </span>
          <span className="ml-auto flex items-center gap-2">
            <button
              onClick={() => bulkApproveMut.mutate()}
              disabled={bulkApproveMut.isPending || bulkRejectMut.isPending}
              className="px-3 py-1.5 text-xs font-medium rounded bg-success-600 text-white hover:bg-success-700 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {bulkApproveMut.isPending ? 'Approving…' : `Approve ${selected.size}`}
            </button>
            <button
              onClick={() => bulkRejectMut.mutate()}
              disabled={bulkApproveMut.isPending || bulkRejectMut.isPending}
              className="px-3 py-1.5 text-xs font-medium rounded border border-danger-300 text-danger-700 hover:bg-danger-50 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <XCircle className="h-3.5 w-3.5" />
              {bulkRejectMut.isPending ? 'Rejecting…' : `Reject ${selected.size}`}
            </button>
            <button
              onClick={() => setSelected(new Set())}
              className="text-xs text-primary-700 hover:text-primary-900 underline"
            >
              Clear
            </button>
          </span>
        </div>
      )}

      {/* Bulk-action result/error toasts */}
      {bulkApproveMut.isSuccess && bulkApproveMut.data && (
        <div className="mb-3 rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-800 inline-flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4" />
          Approved {bulkApproveMut.data.updated}
          {bulkApproveMut.data.not_pending > 0 && (
            <> · {bulkApproveMut.data.not_pending} already approved / not pending</>
          )}
        </div>
      )}
      {bulkApproveMut.isError && (
        <div className="mb-3 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 inline-flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          Bulk approve failed: {(bulkApproveMut.error as Error).message}
        </div>
      )}
      {bulkRejectMut.isSuccess && bulkRejectMut.data && (
        <div className="mb-3 rounded-lg border border-success-200 bg-success-50 p-3 text-sm text-success-800 inline-flex items-center gap-2">
          <XCircle className="h-4 w-4" />
          Rejected {bulkRejectMut.data.updated}
          {bulkRejectMut.data.not_pending > 0 && (
            <> · {bulkRejectMut.data.not_pending} already rejected / not pending</>
          )}
        </div>
      )}
      {bulkRejectMut.isError && (
        <div className="mb-3 rounded-lg border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700 inline-flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          Bulk reject failed: {(bulkRejectMut.error as Error).message}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-lg border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700 inline-flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          Failed to load: {(error as Error).message}
        </div>
      )}

      {isLoading && <div className="text-sm text-grey-500">Loading…</div>}

      {!isLoading && rows.length === 0 && (
        <div className="rounded-lg border border-grey-200 bg-grey-50 p-8 text-center text-sm text-grey-600">
          {searchActive
            ? <>No pairs match <strong>"{debouncedSearch}"</strong>.</>
            : statusFilter === 'pending'
              ? <>No pairs waiting for review.</>
              : <>No {statusFilter} pairs.</>}
        </div>
      )}

      {rows.length > 0 && (
        <TableScroll className="hidden md:block rounded-lg border border-grey-200">
          <table className="w-full text-sm">
            <thead className="bg-grey-50 text-xs text-grey-600 uppercase">
              <tr>
                <th className="px-3 py-2 text-left w-8">
                  {/* Bulk select header — only enabled when there are pending rows */}
                  <input
                    type="checkbox"
                    checked={allPendingSelected}
                    disabled={selectablePending.length === 0}
                    onChange={toggleAllPending}
                    title={selectablePending.length === 0 ? 'No pending rows to select' : 'Select all pending'}
                    className="cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  />
                </th>
                <th className="px-3 py-2 text-left">Recipient</th>
                <th className="px-3 py-2 text-left">Organisation</th>
                <th className="px-3 py-2 text-left" title="Trust scope: emailing this person about this entity">Entity (trust scope)</th>
                <th className="px-3 py-2 text-left">First seen</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <ReviewRow
                  key={r.review_id}
                  row={r}
                  isSelected={selected.has(r.review_id)}
                  onToggle={() => toggleRow(r.review_id)}
                  onApprove={() => approveMut.mutate({ reviewId: r.review_id })}
                  onReject={() => rejectMut.mutate({ reviewId: r.review_id })}
                  busy={approveMut.isPending || rejectMut.isPending}
                />
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}

      {/* Phones: stacked cards, same rows/actions */}
      {rows.length > 0 && (
        <div className="md:hidden divide-y divide-grey-100 rounded-lg border border-grey-200 bg-surface">
          {rows.map(r => (
            <ReviewCard
              key={r.review_id}
              row={r}
              isSelected={selected.has(r.review_id)}
              onToggle={() => toggleRow(r.review_id)}
              onApprove={() => approveMut.mutate({ reviewId: r.review_id })}
              onReject={() => rejectMut.mutate({ reviewId: r.review_id })}
              busy={approveMut.isPending || rejectMut.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ReviewRowProps {
  row: RecipientReviewRow;
  isSelected: boolean;
  onToggle: () => void;
  onApprove: () => void;
  onReject: () => void;
  busy: boolean;
}

function ReviewRow({ row, isSelected, onToggle, onApprove, onReject, busy }: ReviewRowProps) {
  const statusMeta = STATUS_BADGE[row.status];
  const isPending = row.status === 'pending';

  // The organisation the recipient actually belongs to (employer / PR firm).
  // When this differs from the announcing entity's display name, it's worth
  // flagging visually because the operator may want to think harder before
  // approving (e.g. a PR-firm contact representing many companies).
  const orgDiffersFromEntity = !!row.contact_organization && !!row.entity_display_name &&
    row.contact_organization.toLowerCase() !== row.entity_display_name.toLowerCase();

  return (
    <tr className={`border-t border-grey-100 ${isSelected ? 'bg-primary-50/40' : ''}`}>
      <td className="px-3 py-2 align-top">
        <input
          type="checkbox"
          checked={isSelected}
          disabled={!isPending}
          onChange={onToggle}
          title={!isPending ? 'Bulk actions apply to pending rows only' : ''}
          className="cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed mt-0.5"
        />
      </td>
      {/* Recipient — name + title (when known) on top line, email below */}
      <td className="px-3 py-2 align-top">
        <div className="flex flex-col gap-0.5 min-w-0 max-w-[260px]">
          {row.contact_name ? (
            <div className="text-sm text-grey-900 font-medium truncate" title={row.contact_name}>
              {row.contact_name}
              {row.contact_title && (
                <span className="text-grey-500 font-normal"> · {row.contact_title}</span>
              )}
            </div>
          ) : (
            <div className="text-xs text-grey-400 italic">no contact profile</div>
          )}
          <div className="font-mono text-xs text-grey-700 truncate" title={row.recipient_email}>
            {row.recipient_email}
          </div>
          {row.contact_role && (
            <div className="text-[10px] text-grey-500 uppercase tracking-wide">{row.contact_role.replace(/_/g, ' ')}</div>
          )}
        </div>
      </td>
      {/* Organisation — the recipient's own affiliation (often differs from entity) */}
      <td className="px-3 py-2 align-top text-sm text-grey-700 max-w-[200px]">
        {row.contact_organization ? (
          <div className="flex flex-col gap-0.5">
            <span className="truncate" title={row.contact_organization}>
              {row.contact_organization}
            </span>
            {orgDiffersFromEntity && (
              <span className="text-[10px] text-warning-700 font-medium uppercase tracking-wide" title="Recipient's organisation differs from the announcing entity — typically a PR firm or external representative.">
                3rd party
              </span>
            )}
          </div>
        ) : (
          <span className="text-grey-400">—</span>
        )}
      </td>
      {/* Entity — the trust scope (announcing company); approval here = trust for this entity only */}
      <td className="px-3 py-2 align-top text-sm text-grey-700">
        <div className="flex flex-col gap-0.5 min-w-0 max-w-[200px]">
          <span className="truncate" title={row.entity_display_name || ''}>
            {row.entity_display_name || '—'}
          </span>
          {row.entity_identifier && (
            <span className="font-mono text-[11px] text-grey-500">{row.entity_identifier}</span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top text-xs text-grey-600 truncate max-w-[220px]" title={row.first_seen_article_title || ''}>
        {row.first_seen_article_title || <span className="text-grey-400">—</span>}
      </td>
      <td className="px-3 py-2 align-top">
        <div className="flex flex-col gap-1">
          <span className={`inline-flex items-center self-start px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${statusMeta.cls}`}>
            {statusMeta.label}
          </span>
          {row.reviewed_at && (
            <span
              className="text-[10px] text-grey-500"
              title={`Reviewed ${new Date(row.reviewed_at).toLocaleString()} by ${row.reviewed_by ?? '—'}`}
            >
              {formatRelativeShort(row.reviewed_at)}
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 align-top text-right">
        {isPending ? (
          <div className="inline-flex items-center gap-1">
            <button
              onClick={onApprove}
              disabled={busy}
              className="px-2 py-1 text-xs font-medium rounded bg-success-50 text-success-700 hover:bg-success-100 disabled:opacity-50 inline-flex items-center gap-1 border border-success-200"
              title="Approve this (recipient, entity) pair. Trusted across all future drafts."
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Approve
            </button>
            <button
              onClick={onReject}
              disabled={busy}
              className="px-2 py-1 text-xs font-medium rounded text-danger-700 hover:bg-danger-50 disabled:opacity-50 inline-flex items-center gap-1 border border-danger-200"
              title="Reject this pair. Soft-locks them from future drafts (operator can override per-draft)."
            >
              <XCircle className="h-3.5 w-3.5" /> Reject
            </button>
          </div>
        ) : (
          <span className="text-xs text-grey-400">decided</span>
        )}
      </td>
    </tr>
  );
}

// Phone card — same data + actions as ReviewRow, stacked. The trust-scope and
// 3rd-party semantics that live in `title=` tooltips on desktop are surfaced
// as visible text here (tooltips don't exist on touch).
function ReviewCard({ row, isSelected, onToggle, onApprove, onReject, busy }: ReviewRowProps) {
  const statusMeta = STATUS_BADGE[row.status];
  const isPending = row.status === 'pending';
  const orgDiffersFromEntity = !!row.contact_organization && !!row.entity_display_name &&
    row.contact_organization.toLowerCase() !== row.entity_display_name.toLowerCase();

  return (
    <div className={`p-3 ${isSelected ? 'bg-primary-50/40' : ''}`}>
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={isSelected}
          disabled={!isPending}
          onChange={onToggle}
          className="h-4 w-4 mt-0.5 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              {row.contact_name ? (
                <div className="text-sm text-grey-900 font-medium">
                  {row.contact_name}
                  {row.contact_title && (
                    <span className="text-grey-500 font-normal"> · {row.contact_title}</span>
                  )}
                </div>
              ) : (
                <div className="text-xs text-grey-400 italic">no contact profile</div>
              )}
              <div className="font-mono text-xs text-grey-700 break-all">{row.recipient_email}</div>
            </div>
            <span className={`inline-flex items-center flex-shrink-0 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${statusMeta.cls}`}>
              {statusMeta.label}
            </span>
          </div>
          <div className="mt-1.5 space-y-0.5 text-xs text-grey-600">
            {row.contact_organization && (
              <div>
                Org: {row.contact_organization}
                {orgDiffersFromEntity && (
                  <span className="ml-1.5 text-warning-700 font-medium uppercase text-[10px]">3rd party — differs from entity</span>
                )}
              </div>
            )}
            <div>
              Trust scope: {row.entity_display_name || '—'}
              {row.entity_identifier && <span className="font-mono text-grey-500"> ({row.entity_identifier})</span>}
            </div>
            {row.first_seen_article_title && (
              <div className="text-grey-500 line-clamp-2">First seen: {row.first_seen_article_title}</div>
            )}
            {row.reviewed_at && (
              <div className="text-grey-500">
                Reviewed {formatRelativeShort(row.reviewed_at)}{row.reviewed_by ? ` by ${row.reviewed_by}` : ''}
              </div>
            )}
          </div>
          {isPending && (
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={onApprove}
                disabled={busy}
                className="px-3 py-2 text-xs font-medium rounded bg-success-50 text-success-700 hover:bg-success-100 active:bg-success-100 disabled:opacity-50 inline-flex items-center gap-1 border border-success-200"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Approve
              </button>
              <button
                onClick={onReject}
                disabled={busy}
                className="px-3 py-2 text-xs font-medium rounded text-danger-700 hover:bg-danger-50 active:bg-danger-50 disabled:opacity-50 inline-flex items-center gap-1 border border-danger-200"
              >
                <XCircle className="h-3.5 w-3.5" /> Reject
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatRelativeShort(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return new Date(iso).toLocaleDateString();
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
